import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Toaster } from './components/ui/sonner';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import BottomNav from './components/BottomNav';

// Pages
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ChildDashboard from './pages/ChildDashboard';
import ParentDashboard from './pages/ParentDashboard';
import AdminDashboard from './pages/AdminDashboard';
import MissionsPage from './pages/MissionsPage';
import MissionDetailPage from './pages/MissionDetailPage';
import AnimalLibraryPage from './pages/AnimalLibraryPage';
import AnimalDetailPage from './pages/AnimalDetailPage';
import LeaderboardPage from './pages/LeaderboardPage';
import ContentHubPage from './pages/ContentHubPage';
import AboutPage from './pages/AboutPage';
import EducatorDashboard from './pages/EducatorDashboard';
import JoinClassroomPage from './pages/JoinClassroomPage';
import StudentClassroomPage from './pages/StudentClassroomPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import InviteRegisterPage from './pages/InviteRegisterPage';
import WorksheetsPage from './pages/WorksheetsPage';
import GamesHubPage from './pages/GamesHubPage';
import MemoryMatchGame from './pages/MemoryMatchGame';
import QuizRaceGame from './pages/QuizRaceGame';
import WordScrambleGame from './pages/WordScrambleGame';
import HabitatBuilderGame from './pages/HabitatBuilderGame';
import SpotDifferenceGame from './pages/SpotDifferenceGame';
import FoodChainGame from './pages/FoodChainGame';
import HabitatsPage from './pages/HabitatsPage';
import HabitatDetailPage from './pages/HabitatDetailPage';
import LocationDetailPage from './pages/LocationDetailPage';
import CustomizeAvatarPage from './pages/CustomizeAvatarPage';
import AdventurePage from './pages/AdventurePage';
import AccountSettingsPage from './pages/AccountSettingsPage';

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles = [] }) => {
    const { user, loading, isAuthenticated } = useAuth();
    
    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[hsl(53,92%,96%)]">
                <div className="brutalist-card p-8 text-center">
                    <div className="w-16 h-16 border-4 border-[hsl(142,71%,45%)] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="font-fredoka text-lg">Loading...</p>
                </div>
            </div>
        );
    }
    
    if (!isAuthenticated) {
        return <Navigate to="/login" replace />;
    }
    
    if (allowedRoles.length > 0 && !allowedRoles.includes(user?.role)) {
        return <Navigate to="/" replace />;
    }
    
    return children;
};

// Dashboard Router - redirects to appropriate dashboard based on role
const DashboardRouter = () => {
    const { user } = useAuth();
    
    switch (user?.role) {
        case 'child':
            return <ChildDashboard />;
        case 'parent':
            return <ParentDashboard />;
        case 'admin':
            return <AdminDashboard />;
        case 'educator':
            return <ParentDashboard />; // Educators use similar dashboard
        default:
            return <Navigate to="/login" replace />;
    }
};

function AppRoutes() {
    const { isAuthenticated, user } = useAuth();
    
    return (
        <Routes>
            {/* Public Routes */}
            <Route path="/" element={<HomePage />} />
            <Route path="/login" element={isAuthenticated ? <Navigate to="/dashboard" /> : <LoginPage />} />
            <Route path="/register" element={isAuthenticated ? <Navigate to="/dashboard" /> : <RegisterPage />} />
            <Route path="/forgot-password" element={isAuthenticated ? <Navigate to="/dashboard" /> : <ForgotPasswordPage />} />
            <Route path="/invite/:token" element={<InviteRegisterPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/adventure" element={<AdventurePage />} />
            
            {/* Protected Routes */}
            <Route path="/dashboard" element={
                <ProtectedRoute>
                    <DashboardRouter />
                </ProtectedRoute>
            } />
            
            <Route path="/missions" element={
                <ProtectedRoute>
                    <MissionsPage />
                </ProtectedRoute>
            } />
            
            <Route path="/missions/:id" element={
                <ProtectedRoute>
                    <MissionDetailPage />
                </ProtectedRoute>
            } />
            
            <Route path="/animals" element={
                <ProtectedRoute>
                    <AnimalLibraryPage />
                </ProtectedRoute>
            } />
            
            <Route path="/animals/:id" element={
                <ProtectedRoute>
                    <AnimalDetailPage />
                </ProtectedRoute>
            } />
            
            <Route path="/leaderboard" element={
                <ProtectedRoute>
                    <LeaderboardPage />
                </ProtectedRoute>
            } />
            
            <Route path="/content" element={
                <ProtectedRoute>
                    <ContentHubPage />
                </ProtectedRoute>
            } />
            
            <Route path="/admin" element={
                <ProtectedRoute allowedRoles={['admin']}>
                    <AdminDashboard />
                </ProtectedRoute>
            } />

            <Route path="/educator" element={
                <ProtectedRoute allowedRoles={['parent', 'educator', 'admin']}>
                    <EducatorDashboard />
                </ProtectedRoute>
            } />

            <Route path="/join-classroom" element={
                <ProtectedRoute>
                    <JoinClassroomPage />
                </ProtectedRoute>
            } />

            <Route path="/my-classroom" element={
                <ProtectedRoute>
                    <StudentClassroomPage />
                </ProtectedRoute>
            } />

            <Route path="/worksheets" element={
                <ProtectedRoute allowedRoles={['parent', 'educator', 'admin']}>
                    <WorksheetsPage />
                </ProtectedRoute>
            } />

            <Route path="/games" element={
                <ProtectedRoute>
                    <GamesHubPage />
                </ProtectedRoute>
            } />

            <Route path="/games/memory-match" element={
                <ProtectedRoute>
                    <MemoryMatchGame />
                </ProtectedRoute>
            } />

            <Route path="/games/quiz-race" element={
                <ProtectedRoute>
                    <QuizRaceGame />
                </ProtectedRoute>
            } />

            <Route path="/games/word-scramble" element={
                <ProtectedRoute>
                    <WordScrambleGame />
                </ProtectedRoute>
            } />

            <Route path="/games/habitat-builder" element={
                <ProtectedRoute>
                    <HabitatBuilderGame />
                </ProtectedRoute>
            } />

            <Route path="/games/spot-difference" element={
                <ProtectedRoute>
                    <SpotDifferenceGame />
                </ProtectedRoute>
            } />

            <Route path="/games/food-chain" element={
                <ProtectedRoute>
                    <FoodChainGame />
                </ProtectedRoute>
            } />

            <Route path="/habitats" element={
                <ProtectedRoute>
                    <HabitatsPage />
                </ProtectedRoute>
            } />

            <Route path="/habitats/:habitatId" element={
                <ProtectedRoute>
                    <HabitatDetailPage />
                </ProtectedRoute>
            } />

            <Route path="/habitats/:habitatId/locations/:locationIndex" element={
                <ProtectedRoute>
                    <LocationDetailPage />
                </ProtectedRoute>
            } />

            <Route path="/customize" element={
                <ProtectedRoute allowedRoles={['child']}>
                    <CustomizeAvatarPage />
                </ProtectedRoute>
            } />

            <Route path="/account" element={
                <ProtectedRoute allowedRoles={['parent', 'educator', 'admin']}>
                    <AccountSettingsPage />
                </ProtectedRoute>
            } />
            
            {/* Fallback */}
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
}

// Global mobile bottom nav — role-aware, hidden on public/auth/game-play pages
const HIDDEN_EXACT = ['/', '/login', '/register', '/forgot-password', '/about', '/adventure'];
const HIDDEN_PREFIX = ['/invite', '/games/', '/admin'];
const GlobalBottomNav = () => {
    const { isAuthenticated } = useAuth();
    const location = useLocation();
    if (!isAuthenticated) return null;
    const path = location.pathname;
    if (HIDDEN_EXACT.includes(path)) return null;
    if (HIDDEN_PREFIX.some((p) => path.startsWith(p))) return null;
    return <BottomNav />;
};

function App() {
    return (
        <AuthProvider>
            <BrowserRouter>
                <AppRoutes />
                <GlobalBottomNav />
                <Toaster position="top-right" richColors />
                <PWAInstallPrompt />
            </BrowserRouter>
        </AuthProvider>
    );
}

export default App;
